import dedent from 'dedent';
import {z} from 'zod';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {defineToolRegistryManifest} from '../../types/base-tool-types';

const toolName = 'check-functions-cms-access' as const;

// No input: the site always comes from the session.
const argumentZod = z.object({});

export type CheckFunctionsCmsAccessArgumentType = z.infer<typeof argumentZod>;

export const checkFunctionsCmsAccessManifest = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'Check Code Components CMS access',
  prettyStatus: {
    executing: 'Checking Code Components CMS access',
    executed: 'Checked Code Components CMS access',
    failed: 'Could not check Code Components CMS access',
  },
  llmTool: {
    name: toolName,
    description: dedent`
      Reports whether this site's Code Components can read the site's CMS through \`ctx.cms\`, and whether the user can turn that on. It changes nothing and needs no consent. Call it before you ask the user anything about CMS access. When you talk to the user, call it "Code Components CMS access".

      The result:
      - \`available: true\`: CMS access is on. Do not ask the user anything. Code Components can use \`ctx.cms\`.
      - \`available: false, canTurnOn: true\`: CMS access is off, and the user can turn it on. Tell them their Code Components will be able to read this site's CMS content, read-only, and ask whether they agree. Always ask, also when the user asked you to turn on CMS access. Only if they agree, call enable-functions-cms-access.
      - \`available: false, canTurnOn: false\`: CMS access is off, and the user can't turn it on. Do not ask for their agreement. \`reason\` and \`instructions\` say what to tell the user.
    `,
    inputSchema: argumentZod,
  },
  type: 'server',
  featureGate: FEATURE_FLAGS.CODE_FUNCTIONS,
  permission: [{resource: 'SiteSymbol', action: 'modify'}],
  confirmBeforeExecuting: false,
  isConcurrencySafe: () => true,
  isReadOnly: () => true,
});
